import { useToast } from '@chakra-ui/react';
import {
  type RegistrationProgressResponse,
  buildStatusVerificationSections,
  extractRegistrationDisplayInfo,
  extractRegistrationSuccessInfo,
  isCompletedRegistrationStatus,
  shouldShowRegistrationSuccessBanner,
} from '@diamond/shared/features/registration/browser';
import axios from 'axios';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { fetchRegistrationProgress } from './fetch-registration-progress';
import { NOO_REGISTRATION_STATUS_PATH } from './noo-registration-status-urls';
import {
  isNooRegistrationStatusAccessRevoked,
  readNooRegistrationStatusToken,
  saveNooRegistrationStatusToken,
} from './registration-token-storage';

export function useNooRegistrationStatus() {
  const router = useRouter();
  const toast = useToast();
  const [progress, setProgress] = useState<RegistrationProgressResponse | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [registrationToken, setRegistrationToken] = useState('');
  const [tokenReady, setTokenReady] = useState(false);

  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    const registrationTokenQuery = router.query['registration_token'];
    const rawQueryToken =
      typeof registrationTokenQuery === 'string'
        ? registrationTokenQuery.trim()
        : '';
    const accessRevoked = isNooRegistrationStatusAccessRevoked();
    const queryToken = accessRevoked ? '' : rawQueryToken;
    const storedToken = readNooRegistrationStatusToken();
    const resolvedToken = queryToken || storedToken;

    if (queryToken) {
      saveNooRegistrationStatusToken(queryToken);
      void router.replace('/noo-registration/status', undefined, {
        shallow: true,
      });
    } else if (accessRevoked && rawQueryToken) {
      void router.replace('/login');
    }

    setRegistrationToken(resolvedToken);
    setTokenReady(true);
  }, [router.isReady, router.query['registration_token'], router]);

  const loadStatus = useCallback(async () => {
    if (!registrationToken) {
      setError(
        'Token registrasi tidak ditemukan. Silakan buka halaman ini dari link registrasi.'
      );
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetchRegistrationProgress(registrationToken);
      setProgress(response);
    } catch (fetchError) {
      let message = 'Gagal mengambil status registrasi.';
      if (axios.isAxiosError(fetchError)) {
        const apiMessage = fetchError.response?.data as
          | { message?: string }
          | undefined;
        if (apiMessage?.message) {
          message = apiMessage.message;
        } else if (fetchError.response?.status === 401) {
          message =
            'Token registrasi tidak valid atau sudah kedaluwarsa. Silakan masuk kembali.';
        } else if (fetchError.response?.status === 403) {
          message = 'Akses status registrasi ditolak. Silakan masuk kembali.';
        }
      } else if (fetchError instanceof Error) {
        message = fetchError.message;
      }
      setError(message);
      toast({
        title: 'Gagal memuat status',
        description: message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [registrationToken, toast]);

  useEffect(() => {
    if (!tokenReady) {
      return;
    }

    void loadStatus();
  }, [tokenReady, loadStatus]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const syncTokenAfterNavigation = () => {
      const storedToken = readNooRegistrationStatusToken();
      if (storedToken) {
        return;
      }

      setProgress(null);
      setRegistrationToken('');
      setError(
        'Sesi berakhir. Silakan masuk kembali untuk melihat status registrasi.'
      );
      setIsLoading(false);

      if (router.pathname.startsWith(NOO_REGISTRATION_STATUS_PATH)) {
        void router.replace('/login');
      }
    };

    window.addEventListener('pageshow', syncTokenAfterNavigation);
    return () => {
      window.removeEventListener('pageshow', syncTokenAfterNavigation);
    };
  }, [router]);

  const displayInfo = useMemo(
    () => (progress ? extractRegistrationDisplayInfo(progress) : null),
    [progress]
  );
  const verificationSections = useMemo(
    () => (progress ? buildStatusVerificationSections(progress) : []),
    [progress]
  );
  const successInfo = useMemo(
    () => (progress ? extractRegistrationSuccessInfo(progress) : null),
    [progress]
  );
  const showSuccessBanner = progress
    ? shouldShowRegistrationSuccessBanner(progress)
    : false;
  const isStatusVerificationView =
    progress?.registration_status === 'pending_review' ||
    (progress ? isCompletedRegistrationStatus(progress) : false) ||
    showSuccessBanner;

  return {
    progress,
    isLoading,
    error,
    registrationToken,
    loadStatus,
    displayInfo,
    verificationSections,
    successInfo,
    showSuccessBanner,
    isStatusVerificationView,
  };
}
